import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from 'components/Dialog';
import Button from '@mui/material/Button';
import { askUnmuteRemoteVideo } from 'features/streaming/actions';
import { UserId } from 'features/users/types';
import { selectActiveUserById } from 'features/users/usersSlice';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { useEffect } from 'react';

const UnmuteRemoteVideoConfirmation = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalData = useAppSelector(selectActiveModalData);

  const userId = modalData.userId as UserId;
  const user = useAppSelector((state) => selectActiveUserById(state, userId));

  const { hasPermissions } = usePermissions();

  const canRemotelyUnmute = hasPermissions(PermissionTypes.askRemoteUnmute, {
    targetRole: user?.role,
  });

  useEffect(() => {
    if (!user || !canRemotelyUnmute) {
      dispatch(closeModal('unmuteRemoteVideoConfirmation'));
    }
  }, [user, canRemotelyUnmute, dispatch]);

  if (!user) {
    return null;
  }

  const handleOk = () => {
    dispatch(askUnmuteRemoteVideo(user.id));
    dispatch(closeModal('unmuteRemoteVideoConfirmation'));
  };

  const handleCancel = () => {
    dispatch(closeModal('unmuteRemoteVideoConfirmation'));
  };

  return (
    <Dialog
      open
      maxWidth="xs"
      aria-labelledby="remote-video-unmute-confirm-title"
      aria-describedby="remote-video-unmute-confirm-description"
    >
      <DialogTitle id="remote-video-unmute-confirm-title">
        {t('unmute_remote_video_confirm.title', { username: user.name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="remote-video-unmute-confirm-description">
          {t('unmute_remote_video_confirm.description', { username: user.name })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('unmute_remote_video_confirm.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk}>{t('unmute_remote_video_confirm.btn_ok_label')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnmuteRemoteVideoConfirmation;
